import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Target, Plus, Trash2, Sparkles, Check, Users, Pencil, X } from "lucide-react";
import { API } from "../../App";

const STATUS = { actif: "Actif", atteint: "Atteint", abandonne: "Abandonné" };
const STATUS_CLS = { actif: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", atteint: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", abandonne: "bg-muted text-muted-foreground" };
const EMPTY = { problem: "", goal: "", measure: "", baseline: "", target: "", realistic: true, shared: false, status: "actif", progress: 0, notes: "" };
const inputCls = "px-2 py-1.5 text-sm border rounded-lg bg-background border-border w-full";

const GoalForm = ({ initial, onSave, onCancel, saving }) => {
  const [g, setG] = useState({ ...EMPTY, ...initial });
  const f = (k) => ({ value: g[k] ?? "", onChange: (e) => setG({ ...g, [k]: e.target.value }) });
  return (
    <div className="rounded-lg border bg-background p-3 space-y-2" data-testid="goal-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <Input {...f("problem")} placeholder="Problème source (plainte du patient) — ex. « Je veux me sentir mieux »" data-testid="goal-problem-input" />
        <Input {...f("goal")} placeholder="Objectif spécifique et mesurable — ex. « Réduire les crises d'anxiété de 5 à 1-2/semaine »" data-testid="goal-goal-input" />
        <Input {...f("measure")} placeholder="Indicateur de mesure (fréquence, échelle 0-10, durée…)" data-testid="goal-measure-input" />
        <div className="grid grid-cols-2 gap-2">
          <Input {...f("baseline")} placeholder="Niveau actuel" data-testid="goal-baseline-input" />
          <Input {...f("target")} placeholder="Cible" data-testid="goal-target-input" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!g.realistic} onChange={(e) => setG({ ...g, realistic: e.target.checked })} data-testid="goal-realistic-check" /> Réaliste</label>
        <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!g.shared} onChange={(e) => setG({ ...g, shared: e.target.checked })} data-testid="goal-shared-check" /> Partagé avec le patient</label>
        <select className="px-2 py-1 text-sm border rounded-lg bg-background border-border" value={g.status} onChange={(e) => setG({ ...g, status: e.target.value })} data-testid="goal-status-select">
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="flex items-center gap-2 flex-1 min-w-[160px]">Progression <input type="range" min={0} max={100} value={g.progress} onChange={(e) => setG({ ...g, progress: Number(e.target.value) })} className="flex-1" data-testid="goal-progress-range" /> <span className="w-10 text-right tabular-nums">{g.progress}%</span></label>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>Annuler</Button>
        <Button size="sm" onClick={() => onSave(g)} disabled={saving || !g.goal.trim()} data-testid="goal-save-btn"><Check className="w-3.5 h-3.5 mr-1" /> Enregistrer</Button>
      </div>
    </div>
  );
};

export const TherapyGoals = ({ patientId, onChanged }) => {
  const [goals, setGoals] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | goal_id
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => { axios.get(`${API}/patients/${patientId}/goals`, { withCredentials: true }).then((r) => setGoals(r.data)).catch(() => {}); }, [patientId]);

  const save = async (g) => {
    setBusy("save");
    try {
      if (editing === "new" || !editing) {
        const r = await axios.post(`${API}/patients/${patientId}/goals`, g, { withCredentials: true });
        setGoals([...goals, r.data]);
      } else {
        const r = await axios.put(`${API}/goals/${editing}`, g, { withCredentials: true });
        setGoals(goals.map((x) => (x.goal_id === editing ? r.data : x)));
      }
      setEditing(null); toast.success("Objectif enregistré"); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet objectif ?")) return;
    try { await axios.delete(`${API}/goals/${id}`, { withCredentials: true }); setGoals(goals.filter((g) => g.goal_id !== id)); onChanged?.(); }
    catch { toast.error("Erreur"); }
  };

  const suggest = async () => {
    setBusy("suggest");
    try {
      const r = await axios.post(`${API}/patients/${patientId}/goals/suggest`, {}, { withCredentials: true });
      setSuggestions(r.data.goals); toast.success(`${r.data.goals.length} objectifs proposés à partir de ${r.data.sessions_used} séance(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const adopt = async (s, i) => {
    setBusy("save");
    try {
      const r = await axios.post(`${API}/patients/${patientId}/goals`, { ...EMPTY, problem: s.problem || "", goal: s.goal, measure: s.measure || "", baseline: s.baseline || "", target: s.target || "", notes: s.rationale || "" }, { withCredentials: true });
      setGoals([...goals, r.data]); setSuggestions(suggestions.filter((_, j) => j !== i)); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid="therapy-goals">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="font-medium flex items-center gap-2"><Target className="w-4 h-4 text-primary" /> Objectifs thérapeutiques <span className="text-xs text-muted-foreground font-normal">(méthode J. Beck — séances 2 à 4)</span></h4>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={suggest} disabled={busy !== ""} className="gap-1.5" data-testid="goals-suggest-btn"><Sparkles className="w-3.5 h-3.5" /> {busy === "suggest" ? "Analyse..." : "Proposer des objectifs (IA)"}</Button>
          <Button size="sm" onClick={() => setEditing("new")} disabled={editing === "new"} className="gap-1.5" data-testid="goals-add-btn"><Plus className="w-3.5 h-3.5" /> Ajouter</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Spécifiques (mesurables) · Réalistes · Partagés avec le patient · Liés à sa liste de problèmes.</p>
      {editing === "new" && <GoalForm initial={{}} onSave={save} onCancel={() => setEditing(null)} saving={busy === "save"} />}
      {suggestions.length > 0 && (
        <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="goal-suggestions">
          <div className="flex items-center justify-between"><p className="text-xs font-medium">Propositions de l'IA — à discuter et convenir avec le patient</p><Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => setSuggestions([])}><X className="w-3.5 h-3.5" /></Button></div>
          {suggestions.map((s, i) => (
            <div key={i} className="flex items-start justify-between gap-2 text-sm bg-background rounded-md p-2" data-testid={`goal-suggestion-${i}`}>
              <div><p className="font-medium">{s.goal}</p><p className="text-xs text-muted-foreground">{s.problem && <>Problème : {s.problem} · </>}{s.measure && <>Mesure : {s.measure} · </>}{s.baseline && <>{s.baseline} → </>}{s.target}</p>{s.rationale && <p className="text-xs italic text-muted-foreground mt-0.5">{s.rationale}</p>}</div>
              <Button size="sm" variant="outline" onClick={() => adopt(s, i)} disabled={busy !== ""} data-testid={`goal-adopt-${i}`}><Plus className="w-3.5 h-3.5 mr-1" /> Adopter</Button>
            </div>
          ))}
        </div>
      )}
      {goals.length === 0 && editing !== "new" && <p className="text-sm text-muted-foreground text-center py-4" data-testid="goals-empty">Aucun objectif défini. Ajoutez-en ou laissez l'IA en proposer à partir des premières séances.</p>}
      <div className="space-y-2">
        {goals.map((g) => editing === g.goal_id ? (
          <GoalForm key={g.goal_id} initial={g} onSave={save} onCancel={() => setEditing(null)} saving={busy === "save"} />
        ) : (
          <div key={g.goal_id} className="rounded-lg border p-3 space-y-1.5" data-testid={`goal-${g.goal_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium">{g.goal}</p>
                {g.problem && <p className="text-xs text-muted-foreground">Problème source : {g.problem}</p>}
                <p className="text-xs text-muted-foreground">{g.measure && <>Mesure : {g.measure}</>}{(g.baseline || g.target) && <> · {g.baseline || "?"} → {g.target || "?"}</>}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_CLS[g.status] || ""}`}>{STATUS[g.status] || g.status}</span>
                {g.shared && <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 inline-flex items-center gap-1"><Users className="w-3 h-3" /> Partagé</span>}
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(g.goal_id)} data-testid={`goal-edit-${g.goal_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => remove(g.goal_id)} data-testid={`goal-delete-${g.goal_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${g.progress || 0}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};
